// CareerPathAdvisor.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { generateCareerGuidance } from '../services/openaiService';
import './CareerPathAdvisor.css';
import Header from './Header';
import Footer from './Footer';

const CareerPathAdvisor = () => {
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    skills: [],
    interests: [],
    education: '',
    experience: '',
    goals: '',
    industry: ''
  });
  
  const [currentSkill, setCurrentSkill] = useState('');
  const [currentInterest, setCurrentInterest] = useState('');
  const [careerGuidance, setCareerGuidance] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState('');
  const [showAuthModal, setShowAuthModal] = useState(false);

  const handleNavigateToHome = () => {
    navigate('/', { replace: true });
  };

  const handleAddSkill = () => {
    if (currentSkill.trim() && !formData.skills.includes(currentSkill.trim())) {
      setFormData({
        ...formData,
        skills: [...formData.skills, currentSkill.trim()]
      });
      setCurrentSkill('');
    }
  };

  const handleRemoveSkill = (skillToRemove) => {
    setFormData({
      ...formData,
      skills: formData.skills.filter(skill => skill !== skillToRemove)
    });
  };

  const handleAddInterest = () => {
    if (currentInterest.trim() && !formData.interests.includes(currentInterest.trim())) {
      setFormData({
        ...formData,
        interests: [...formData.interests, currentInterest.trim()]
      });
      setCurrentInterest('');
    }
  };

  const handleRemoveInterest = (interestToRemove) => {
    setFormData({
      ...formData,
      interests: formData.interests.filter(interest => interest !== interestToRemove)
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    setError('');
  };

  const handleKeyPress = (e, type) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (type === 'skill') handleAddSkill();
      if (type === 'interest') handleAddInterest();
    }
  };

  const handleGenerateGuidance = async () => {
    // Validate form
    if (!formData.skills.length || !formData.interests.length || 
        !formData.education || !formData.experience || 
        !formData.goals || !formData.industry) {
      setError('Please fill in all fields');
      return;
    }

    setIsGenerating(true);
    setError('');

    try {
      // Call the OpenAI service to generate career guidance
      const guidance = await generateCareerGuidance(formData);
      setCareerGuidance(guidance);
    } catch (error) {
      console.error('Error generating career guidance:', error);
      setError('Failed to generate career guidance. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="neural-glass-homepage">
      <div className="neural-background"></div>
      
      {/* Floating Geometric Shapes */}
      <div className="geometric-shapes">
        <div className="shape shape-1"></div>
        <div className="shape shape-2"></div>
        <div className="shape shape-3"></div>
        <div className="shape shape-4"></div>
      </div>
      
      <div className="career-advisor-page-container">
        {/* Header */}
        <Header setShowAuthModal={setShowAuthModal} />

        {/* Main Content */}
        <main className="career-advisor-page-main">
          <div className="career-advisor-main-container">
            <div className="career-advisor-content">
              {/* Left Side - Input Form */}
              <div className="input-panel">
                <div className="input-form-container glass">
                  <div className="section-header">
                    <h3>Career Profile</h3>
                  </div>
                  
                  {/* Skills Input */}
                  <div className="input-group">
                    <label>Skills</label>
                    <div className="tag-input">
                      <input
                        type="text"
                        value={currentSkill}
                        onChange={(e) => setCurrentSkill(e.target.value)}
                        onKeyPress={(e) => handleKeyPress(e, 'skill')}
                        placeholder="Add a skill and press Enter"
                        disabled={isGenerating}
                      />
                      <button 
                        onClick={handleAddSkill}
                        disabled={isGenerating || !currentSkill.trim()}
                        className="add-button"
                      >
                        Add
                      </button>
                    </div>
                    <div className="tag-list">
                      {formData.skills.map((skill, index) => (
                        <span key={index} className="tag">
                          {skill}
                          <button className="remove-button"
                            onClick={() => handleRemoveSkill(skill)}
                            disabled={isGenerating}
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  {/* Interests Input */}
                  <div className="input-group">
                    <label>Interests</label>
                    <div className="tag-input">
                      <input
                        type="text"
                        value={currentInterest}
                        onChange={(e) => setCurrentInterest(e.target.value)}
                        onKeyPress={(e) => handleKeyPress(e, 'interest')}
                        placeholder="Add an interest and press Enter"
                        disabled={isGenerating}
                      />
                      <button 
                        onClick={handleAddInterest}
                        disabled={isGenerating || !currentInterest.trim()}
                        className="add-button"
                      >
                        Add
                      </button>
                    </div>
                    <div className="tag-list">
                      {formData.interests.map((interest, index) => (
                        <span key={index} className="tag">
                          {interest}
                          <button className="remove-button"
                            onClick={() => handleRemoveInterest(interest)}
                            disabled={isGenerating}
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  {/* Education Input */}
                  <div className="input-group">
                    <label>Education Level</label>
                    <select 
                      name="education" 
                      value={formData.education} 
                      onChange={handleInputChange}
                      disabled={isGenerating}
                    >
                      <option value="">Select education level</option>
                      <option value="High School">High School</option>
                      <option value="Associate Degree">Associate Degree</option>
                      <option value="Bachelor's Degree">Bachelor's Degree</option>
                      <option value="Master's Degree">Master's Degree</option>
                      <option value="Doctorate">Doctorate</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  
                  {/* Experience Input */}
                  <div className="input-group">
                    <label>Years of Experience</label>
                    <input
                      type="number"
                      name="experience"
                      value={formData.experience}
                      onChange={handleInputChange}
                      min="0"
                      max="50"
                      placeholder="Enter years of experience"
                      disabled={isGenerating}
                    />
                  </div>
                  
                  {/* Industry Input */}
                  <div className="input-group">
                    <label>Preferred Industry</label>
                    <select 
                      name="industry" 
                      value={formData.industry} 
                      onChange={handleInputChange}
                      disabled={isGenerating}
                    >
                      <option value="">Select industry</option>
                      <option value="Technology">Technology</option>
                      <option value="Healthcare">Healthcare</option>
                      <option value="Finance">Finance</option>
                      <option value="Education">Education</option>
                      <option value="Marketing">Marketing</option>
                      <option value="Engineering">Engineering</option>
                      <option value="Creative Arts">Creative Arts</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  
                  {/* Goals Input */}
                  <div className="input-group">
                    <label>Career Goals</label>
                    <textarea
                      name="goals"
                      value={formData.goals}
                      onChange={handleInputChange}
                      placeholder="Describe your career goals and aspirations..."
                      rows="4"
                      disabled={isGenerating}
                    ></textarea>
                  </div>
                  
                  <button 
                    onClick={handleGenerateGuidance} 
                    className="generate-btn"
                    disabled={isGenerating}
                  >
                    {isGenerating ? (
                      <span className="loading">
                        <span className="typing-indicator">
                          <span></span>
                          <span></span>
                          <span></span>
                        </span>
                        Generating...
                      </span>
                    ) : (
                      'Generate Career Path'
                    )}
                  </button>
                </div>
              </div>

              {/* Right Side - Career Guidance */}
              <div className="guidance-panel">
                <div className="guidance-container glass">
                  <div className="section-header">
                    <h3>Career Guidance</h3>
                  </div>
                  <div className="guidance-content">
                    {isGenerating ? (
                      <div className="loading-placeholder">
                        <span className="typing-indicator">
                          <span></span>
                          <span></span>
                          <span></span>
                        </span>
                        Analyzing your profile and generating personalized career guidance...
                      </div>
                    ) : careerGuidance ? (
                      <div className="guidance-text">
                        <pre>{careerGuidance}</pre>
                      </div>
                    ) : (
                      <div className="placeholder">
                        Your personalized career guidance will appear here after you fill out the form and click "Generate Career Path".
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {error && (
              <div className="error-message glass">
                {error}
              </div>
            )}
          </div>
        </main>

        {/* Information Sections */}
        <section className="info-section glass">
          <h2 className="section-title">How Career Path Guidance Works</h2>
          <div className="info-grid">
            <div className="info-card glass">
              <div className="info-icon">🎯</div>
              <h3>Personalized Analysis</h3>
              <p>Our AI analyzes your unique combination of skills, interests, education, and experience to identify career paths that align with your profile and aspirations.</p>
            </div>
            <div className="info-card glass">
              <div className="info-icon">🚀</div>
              <h3>Growth Opportunities</h3>
              <p>Discover emerging roles and industries that match your skillset, along with actionable steps to transition or advance in your chosen career direction.</p>
            </div>
            <div className="info-card glass">
              <div className="info-icon">📊</div>
              <h3>Market-Driven Insights</h3>
              <p>Receive guidance based on current job market trends, salary expectations, and in-demand skills to make informed career decisions.</p>
            </div>
          </div>
        </section>

        <section className="career-paths-section glass">
          <h2 className="section-title">Popular Career Paths by Industry</h2>
          <div className="paths-grid">
            <div className="path-card glass">
              <h4>💻 Technology</h4>
              <ul>
                <li>Software Developer</li>
                <li>Data Scientist</li>
                <li>Cloud Architect</li>
                <li>Cybersecurity Specialist</li>
                <li>AI/ML Engineer</li>
              </ul>
            </div>
            <div className="path-card glass">
              <h4>🏥 Healthcare</h4>
              <ul>
                <li>Registered Nurse</li>
                <li>Healthcare Administrator</li>
                <li>Medical Technologist</li>
                <li>Physical Therapist</li>
                <li>Health Informatics Specialist</li>
              </ul>
            </div>
            <div className="path-card glass">
              <h4>💰 Finance</h4>
              <ul>
                <li>Financial Analyst</li>
                <li>Investment Banker</li>
                <li>Risk Manager</li>
                <li>Financial Planner</li>
                <li>Fintech Specialist</li>
              </ul>
            </div>
            <div className="path-card glass">
              <h4>🎨 Creative Arts</h4>
              <ul>
                <li>UX/UI Designer</li>
                <li>Content Creator</li>
                <li>Brand Strategist</li>
                <li>Creative Director</li>
                <li>Digital Artist</li>
              </ul>
            </div>
            <div className="path-card glass">
              <h4>🎓 Education</h4>
              <ul>
                <li>Curriculum Developer</li>
                <li>Instructional Designer</li>
                <li>School Administrator</li>
                <li>Education Consultant</li>
                <li>EdTech Specialist</li>
              </ul>
            </div>
            <div className="path-card glass">
              <h4>📢 Marketing</h4>
              <ul>
                <li>Digital Marketing Manager</li>
                <li>SEO Specialist</li>
                <li>Social Media Strategist</li>
                <li>Marketing Analyst</li>
                <li>Growth Hacker</li>
              </ul>
            </div>
            <div className="path-card glass">
              <h4>⚙️ Engineering</h4>
              <ul>
                <li>Mechanical Engineer</li>
                <li>Civil Engineer</li>
                <li>Electrical Engineer</li>
                <li>Systems Engineer</li>
                <li>Automation Engineer</li>
              </ul>
            </div>
            <div className="path-card glass">
              <h4>🏢 Business & Consulting</h4>
              <ul>
                <li>Management Consultant</li>
                <li>Business Analyst</li>
                <li>Operations Manager</li>
                <li>Project Manager</li>
                <li>Strategy Consultant</li>
              </ul>
            </div>
            <div className="path-card glass">
              <h4>⚖️ Legal</h4>
              <ul>
                <li>Corporate Lawyer</li>
                <li>Paralegal</li>
                <li>Compliance Officer</li>
                <li>Legal Consultant</li>
                <li>Contract Manager</li>
              </ul>
            </div>
            <div className="path-card glass">
              <h4>🔬 Science & Research</h4>
              <ul>
                <li>Research Scientist</li>
                <li>Lab Technician</li>
                <li>Clinical Research Associate</li>
                <li>Environmental Scientist</li>
                <li>Biotech Specialist</li>
              </ul>
            </div>
          </div>
        </section>

        <section className="skills-section glass">
          <h2 className="section-title">Skills Development Roadmap</h2>
          <div className="roadmap-grid">
            <div className="roadmap-card glass">
              <div className="roadmap-step">1</div>
              <h4>Assess Current Skills</h4>
              <p>Identify your existing strengths and areas for improvement through self-assessment and feedback from peers or mentors.</p>
            </div>
            <div className="roadmap-card glass">
              <div className="roadmap-step">2</div>
              <h4>Identify Skill Gaps</h4>
              <p>Compare your current skills with requirements for your target career path to determine what you need to learn.</p>
            </div>
            <div className="roadmap-card glass">
              <div className="roadmap-step">3</div>
              <h4>Create Learning Plan</h4>
              <p>Develop a structured plan with online courses, certifications, workshops, or formal education programs.</p>
            </div>
            <div className="roadmap-card glass">
              <div className="roadmap-step">4</div>
              <h4>Gain Practical Experience</h4>
              <p>Apply new skills through projects, internships, volunteer work, or side projects to build your portfolio.</p>
            </div>
            <div className="roadmap-card glass">
              <div className="roadmap-step">5</div>
              <h4>Network & Connect</h4>
              <p>Build relationships with professionals in your target field through LinkedIn, industry events, and professional organizations.</p>
            </div>
          </div>
        </section>

        <section className="tips-section glass">
          <h2 className="section-title">Career Development Tips</h2>
          <div className="tips-columns">
            <div className="tip-column glass">
              <h4>Building Your Brand</h4>
              <ul>
                <li>Create a professional online presence across LinkedIn, GitHub, or portfolio sites</li>
                <li>Share your knowledge through blog posts, articles, or speaking engagements</li>
                <li>Develop a unique value proposition that sets you apart from others</li>
                <li>Maintain consistency in your messaging and professional image</li>
              </ul>
            </div>
            <div className="tip-column glass">
              <h4>Networking Strategies</h4>
              <ul>
                <li>Attend industry conferences, meetups, and professional events regularly</li>
                <li>Engage meaningfully on professional social media platforms</li>
                <li>Seek mentorship from experienced professionals in your field</li>
                <li>Offer help to others and build genuine professional relationships</li>
              </ul>
            </div>
            <div className="tip-column glass">
              <h4>Career Transitions</h4>
              <ul>
                <li>Research your target industry thoroughly before making a move</li>
                <li>Identify transferable skills from your current role</li>
                <li>Consider taking on freelance or contract work in your new field</li>
                <li>Be patient with the transition process and celebrate small wins</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Footer */}
        <Footer></Footer>
      </div>
    </div>
  );
};

export default CareerPathAdvisor;